import { useState } from "react";
import "./style.css";
import CarouselArrow from "../../CarouselArrow/CarouselArrow";
import ImagePickerField from "../../Admin/ImagePickerField/ImagePickerField";
import { getImageUrl, getVideoEmbedUrl } from "../../../utils/media";

// Fallback enquanto o backend nao retorna itens.
const FALLBACK_ITEMS = [];

const EMPTY_ITEM = { title: "", subtitle: "", text: "", image: "", video: null };

// Schema real (models/Metodologia.js): workshops = { title, text, items[] },
// cada item com sua propria imagem ({ url, public_id }) e video opcional.
export default function Oficinas({
  title,
  text,
  items: itemsProp,
  isEditing,
  onChange,
  onItemsChange,
}) {
  const items = Array.isArray(itemsProp)
    ? [...itemsProp].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : isEditing
      ? []
      : FALLBACK_ITEMS;
  const [activeIndex, setActiveIndex] = useState(0);

  const total = items.length;
  const safeIndex = Math.min(activeIndex, Math.max(total - 1, 0));
  const atual = items[safeIndex] || EMPTY_ITEM;
  const imageUrl = getImageUrl(atual?.image);
  const workshopLink = typeof atual?.video === "string" ? atual.video : atual?.video?.url;
  const videoPreviewUrl = getVideoEmbedUrl(atual?.video?.url || atual?.video);
  const introText = typeof text === "string" ? text.trim() : "";

  const irParaAnterior = () => {
    setActiveIndex((i) => (i === 0 ? total - 1 : i - 1));
  };

  const irParaProximo = () => {
    setActiveIndex((i) => (i === total - 1 ? 0 : i + 1));
  };

  const updateItem = (index, field, value) => {
    onItemsChange(
      items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const addItem = () => {
    const updated = [...items, { ...EMPTY_ITEM }];
    onItemsChange(updated);
    setActiveIndex(updated.length - 1);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    onItemsChange(updated);
    setActiveIndex((prev) => Math.min(prev, Math.max(updated.length - 1, 0)));
  };

  return (
    <section className="oficinas">
      {isEditing ? (
        <>
          <input
            className="metodologia-page__campo"
            value={title || ""}
            onChange={(e) => onChange("title", e.target.value)}
          />
          <textarea
            className="metodologia-page__campo"
            value={text || ""}
            onChange={(e) => onChange("text", e.target.value)}
          />
        </>
      ) : (
        <>
          <h2 className="oficinas__titulo">{title ?? "Oficinas"}</h2>
          {introText && <p className="oficinas__intro">{introText}</p>}
        </>
      )}

      <div className="oficinas__carrossel">
        <CarouselArrow
          direction="prev"
          className="oficinas__seta"
          onClick={irParaAnterior}
          ariaLabel="Oficina anterior"
          disabled={total < 2}
        />

        <div className="oficinas__card">
          <div className="oficinas__texto">
            <div className="oficinas__cabecalho">
              <h3>{atual.title}</h3>
              {atual.subtitle && (
                <span className="oficinas__subtitulo">{atual.subtitle}</span>
              )}
            </div>

            <div className="oficinas__descricao">
              <p>{atual.text}</p>
            </div>

            {!isEditing && workshopLink && (
              <a
                className="oficinas__link"
                href={workshopLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Acessar oficina
              </a>
            )}
          </div>

          <div className="oficinas__foto">
            {isEditing ? (
              <ImagePickerField
                value={atual.image}
                onChange={(image) => updateItem(safeIndex, "image", image)}
                alt={atual.title || `Oficina ${safeIndex + 1}`}
              />
            ) : imageUrl ? (
              <img src={imageUrl} alt={atual.title} />
            ) : (
              <div className="oficinas__foto-placeholder" />
            )}

          </div>

          {isEditing && (
            <div className="oficinas__conteudo">
              <>
                {total > 1 && (
                  <span className="oficinas__posicao">
                    Oficina {safeIndex + 1} de {total}
                  </span>
                )}
                <input
                  className="metodologia-page__campo"
                  value={atual.title || ""}
                  onChange={(e) => updateItem(safeIndex, "title", e.target.value)}
                  placeholder="Título da oficina (opcional)"
                />
                <input
                  className="metodologia-page__campo"
                  value={atual.subtitle || ""}
                  onChange={(e) =>
                    updateItem(safeIndex, "subtitle", e.target.value)
                  }
                  placeholder="Subtítulo (opcional)"
                />
                <textarea
                  className="metodologia-page__campo"
                  value={atual.text || ""}
                  onChange={(e) => updateItem(safeIndex, "text", e.target.value)}
                  placeholder="Texto da oficina (opcional)"
                />
                <input
                  className="metodologia-page__campo"
                  type="url"
                  value={atual.video?.url || ""}
                  onChange={(e) =>
                    updateItem(
                      safeIndex,
                      "video",
                      e.target.value ? { url: e.target.value } : null,
                    )
                  }
                  placeholder="Link do vídeo do YouTube ou Instagram (opcional)"
                />
                {videoPreviewUrl && (
                  <div className="oficinas__video-preview">
                    <iframe
                      src={videoPreviewUrl}
                      title={`Vídeo da oficina ${atual.title || safeIndex + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                <div className="oficinas__acoes-item">
                  <button
                    type="button"
                    className="oficinas__adicionar"
                    onClick={addItem}
                  >
                    + Adicionar oficina
                  </button>
                  <button
                    type="button"
                    className="oficinas__remover"
                    onClick={() => removeItem(safeIndex)}
                  >
                    Remover esta oficina
                  </button>
                </div>
              </>
            </div>
          )}
        </div>

        <CarouselArrow
          direction="next"
          className="oficinas__seta"
          onClick={irParaProximo}
          ariaLabel="Próxima oficina"
          disabled={total < 2}
        />
      </div>
    </section>
  );
}
